// Calendar card styling — colored by EVENT TYPE (not crew).
// Appointment = dark tint (neutral-200), Task = red tint, Imported = light grey.
// Uses CSS variable tokens for consistency with BIDIT design system.

import {
  ClipboardDocumentListIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/solid';
// Cycle-safe: crewIdentity only reads CREW_COLORS inside functions.
import { crewIdentity } from './crewIdentity';

const TYPE_ICONS = {
  work_order: ClipboardDocumentListIcon,
  appointment: CalendarDaysIcon,
  task: CheckCircleIcon,
  bid: DocumentTextIcon,
  imported: ArrowDownTrayIcon,
};

/** Icon component for an event type. */
export function eventIcon(type) {
  return TYPE_ICONS[type] || CalendarDaysIcon;
}

/**
 * Canonical crew colour palette (20 entries).
 *
 * Historically owned by `components/settings/CrewsPage.js`. Moved here in
 * PT-22 W3 FP3-1 F3 so the same array backs both the Settings palette-picker
 * UI (CrewsPage / CrewForm) AND the picker/display swatches via
 * `resolveCrewColor(crew)`. `CrewsPage.js` re-exports for backward compat.
 *
 * IMPORTANT: index 0 (`'#D72C0D'`, red) is the historical Settings fallback
 * (`crew.color || CREW_COLORS[0]`) that masked crews with no stored colour.
 * `resolveCrewColor` uses the same fallback so a crew missing `crew.color`
 * renders identically in Settings and in the picker — no drift between
 * surfaces.
 */
// IDI-84: WCAG 2.1 §1.4.11 (Non-text Contrast) requires ≥ 3:1 for a solid
// swatch against the adjacent background. Portica's live measurement
// (2026-08-31) found 8 of 20 values under threshold — 4 originally noted
// plus 4 more (indices 1, 3, 10, 14) that had been grouped into a single
// "other 16 — ≥3.0 — ✅" table row without individual checking. Nine
// replacements land in-place; array length and order preserved because
// index 0 (#D72C0D, red) is the historical Settings fallback and
// resolveCrewColor depends on ordering.
//
// Each replacement preserves the hue and saturation of the original and
// reduces value until the swatch clears 3:1 against #F9FAFB (the harder of
// the two grounds, matching hover:bg-gray-50 row state). See the
// palette-wide assertion in crewColors.test.js — that test is what stops
// the class from re-opening the next time a hex is added.
//
// No data migration needed. Crews store a colour SLOT, not a hex; existing
// crew→slot assignments carry through unchanged and simply render darker.
export const CREW_COLORS = [
  '#D72C0D', '#DA7600', '#AE8C11', '#47A24A', '#2E7D32',
  '#0288D1', '#1565C0', '#5C6BC0', '#7B1FA2', '#C2185B',
  '#EC673E', '#BD8739', '#7D995D', '#3B9EAB', '#5295CB',
  '#BA68C8', '#EA608F', '#A1887F', '#78909C', '#455A64',
];

/**
 * Resolve the display colour for a crew record.
 *
 * Single source of truth used by every crew-colour render site:
 *   • Settings › Crews (colour dot in the list, CrewsPage)
 *   • WOCreateSheet crew picker (option swatch, TypeaheadChips variant='identity')
 *   • WorkOrderDetail edit-mode crew picker
 *   • WO save (stamps `wo.crewColor` on the envelope so display surfaces
 *     stay consistent with the picker without a second lookup)
 *
 * @param {object|null|undefined} crew  A crew record (or picker option carrying
 *                                       a `color` field). Only `.color` is read.
 * @returns {string} A CSS colour string. Never empty — falls back to
 *                   `CREW_COLORS[0]` (matches the historical Settings fallback
 *                   in CrewsPage.js:137, so Settings dot ≡ picker swatch even
 *                   for crews whose `color` field is missing on the tenant
 *                   record).
 */
export function resolveCrewColor(crew) {
  if (crew && crew.color) return crew.color;
  return CREW_COLORS[0];
}

/**
 * Deterministic color for a crew (LEGACY — kept for CalendarPage + its test).
 *
 * Do NOT use for new code. This stub predates the stored `crew.color` field
 * and always returns a neutral token regardless of crew identity. New crew-
 * colour render sites should use `resolveCrewColor(crew)` above.
 */
export function crewColor(crewId) {
  if (!crewId) return 'var(--neutral-100)';
  return 'var(--neutral-200)';
}

/** Text color for crew-colored cards. */
export function crewTextColor(crewId) {
  return 'var(--color-text-primary)';
}

/**
 * Visual style for a calendar card — colored by EVENT TYPE.
 * - Appointment: 25% lighter black (neutral-200 bg, dark text)
 * - Task: 25% lighter red (red-100 bg, red-700 text)
 * - Imported/external: light grey (neutral-50 bg, muted text)
 * Returns { backgroundColor, textColor, borderColor, isImported, Icon }.
 */
export function cardStyle(event = {}) {
  const isImported = event.source === 'imported' || event.type === 'imported';
  const type = event.type || 'appointment';

  if (isImported) {
    return {
      backgroundColor: 'var(--neutral-50)',
      textColor: 'var(--color-text-muted)',
      borderColor: 'var(--neutral-300)',
      isImported: true,
      Icon: eventIcon('imported'),
    };
  }

  if (type === 'task') {
    return {
      backgroundColor: 'var(--red-100)',
      textColor: 'var(--red-700)',
      borderColor: 'var(--red-200)',
      isImported: false,
      Icon: eventIcon('task'),
    };
  }

  // Work orders (scheduled jobs): BIDIT black bg with white text.
  // color-chrome is the canonical dark token (neutral-900). White-on-#1F1F1F
  // gives >12:1 contrast — well above WCAG AA (4.5:1) for body text.
  if (type === 'work_order') {
    return {
      backgroundColor: 'var(--color-chrome)',
      textColor: 'var(--white)',
      borderColor: 'var(--color-chrome)',
      isImported: false,
      Icon: eventIcon('work_order'),
    };
  }

  // Default: appointment
  return {
    backgroundColor: 'var(--neutral-200)',
    textColor: 'var(--neutral-900)',
    borderColor: 'var(--neutral-400)',
    isImported: false,
    Icon: eventIcon(type),
  };
}

// ─── IDI-476: work-order cards coloured by their crew's Settings colour ────

/** The crews on a calendar card: `crewIds` when present, else `[crewId]`. */
export function eventCrewIds(event = {}) {
  const raw = Array.isArray(event.crewIds) && event.crewIds.length
    ? event.crewIds
    : [event.crewId ?? event.crew_id];
  const seen = new Set();
  return raw
    .map((id) => (id == null ? '' : String(id).trim()))
    .filter((id) => id && !seen.has(id) && seen.add(id));
}

/** Black or white text, whichever reads on a #rrggbb background. */
export function readableTextOn(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || '').trim());
  if (!m) return '#FFFFFF';
  const n = parseInt(m[1], 16);
  const lin = (c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  const L = 0.2126 * lin((n >> 16) & 255) + 0.7152 * lin((n >> 8) & 255) + 0.0722 * lin(n & 255);
  // Contrast against white vs against black; pick the stronger.
  return (1.05 / (L + 0.05)) >= ((L + 0.05) / 0.05) ? '#FFFFFF' : '#111111';
}

/** A slightly darker shade of a #rrggbb colour, for the card border. */
function darken(hex, amount = 0.2) {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || '').trim());
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  const ch = (v) => Math.max(0, Math.round(v * (1 - amount))).toString(16).padStart(2, '0');
  return `#${ch((n >> 16) & 255)}${ch((n >> 8) & 255)}${ch(n & 255)}`.toUpperCase();
}

/**
 * Card style for a work-order event (IDI-476).
 *  - one crew   → the block is that crew's Settings colour (crewIdentity /
 *                 resolveCrewColor; never a hash colour), readable text,
 *                 border a shade darker;
 *  - 2+ crews   → the neutral/dark chrome block, and `dots` (one per crew) replace
 *                 the type icon;
 *  - no crew    → the existing type style (cardStyle).
 * Returns cardStyle's shape plus `crews: [{ id, name, color }]` and `dots`.
 */
export function workOrderCardStyle(event = {}, crews = []) {
  const base = cardStyle(event);
  const ids = eventCrewIds(event);
  if (base.isImported || ids.length === 0) return { ...base, crews: [], dots: false };

  const resolved = ids.map((id, i) => {
    // Only the first crew can use the card's own name/colour snapshot.
    const snap = i === 0 && String(event.crewId ?? '') === id
      ? { crewId: id, crewName: event.crewName, crewColor: event.crewColor }
      : { crewId: id };
    const ident = crewIdentity(snap, crews);
    return { id, name: ident.name, color: ident.color };
  });

  if (resolved.length === 1) {
    const color = resolved[0].color;
    return {
      ...base,
      backgroundColor: color,
      textColor: readableTextOn(color),
      borderColor: darken(color),
      crews: resolved,
      dots: false,
    };
  }

  // Several crews: the neutral/dark work-order block (BIDIT chrome), with
  // the crew dots carrying the colours.
  return {
    ...base,
    backgroundColor: 'var(--color-chrome)',
    textColor: 'var(--white)',
    borderColor: 'var(--color-chrome)',
    crews: resolved,
    dots: true,
  };
}
